import React, { useCallback, useContext, useEffect } from 'react'
import { interactionStudioAPIController } from 'lib/analytics/interactionStudioUtils'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType, PaginatorState, BundledOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { pluralizeToString } from 'lib/string/pluralize'
import TextButton from 'components/Luxkit/Button/TextButton'
import Caption from 'components/Luxkit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'components/Luxkit/Typography/Heading'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import Group from 'components/utils/Group'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import DropdownSheetFooter from 'components/Luxkit/Dropdown/Sheet/DropdownSheetFooter'
import { BundleSelectedDaysInfo, getBundleSelectedDays } from 'selectors/offerPage/bundledOfferPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

interface Props {
  checkIn?: string;
  offer: App.Offer;
  fetching?: boolean;
  selectedDay?: App.CalendarDay;
  relevantPackages: Array<OfferPackageOptionView>;
  selectedPackageView: OfferPackageOptionView;
  otherAvailability: { [checkIn: string]: boolean };
  interactionStudioData?: App.ISACheckoutData;
  filter: OfferPageFilterType;
  paginator?: PaginatorState;
  complementaryBundleState?: BundledOfferState
  isCalendarAlreadyOpen?: boolean
  bundleSelectedDays: BundleSelectedDaysInfo;
}

function OfferBundleAndSaveCalendarDatePickerDropdownFooter(props: Props) {
  const {
    checkIn,
    offer,
    fetching,
    selectedDay,
    relevantPackages,
    selectedPackageView,
    otherAvailability,
    interactionStudioData,
    filter,
    paginator,
    complementaryBundleState,
    isCalendarAlreadyOpen,
    bundleSelectedDays,
  } = props

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()

  useEffect(() => {
    if (!fetching && checkIn && selectedDay && !selectedDay.hotelAvailable && !otherAvailability[selectedDay.checkIn]) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined })
    }
  }, [checkIn, selectedDay]) // eslint-disable-line react-hooks/exhaustive-deps

  const onApply = useCallback(() => {
    if (complementaryBundleState && !complementaryBundleState?.checkInDate && complementaryBundleState.offer) {
      if (!isCalendarAlreadyOpen) {
        dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'duration' })
      }
      dispatch({ type: OfferPageActions.setPaginatorCurrentOfferId, currentOfferId: complementaryBundleState.offer.id })
      return
    }

    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
    dispatch({ type: OfferPageActions.closeFilter })
    interactionStudioAPIController(filter, interactionStudioData)
  }, [dispatch, onUserAnalyticsAction, interactionStudioData, filter, complementaryBundleState, isCalendarAlreadyOpen])

  const onResetDates = useCallback(() => {
    dispatch({ type: OfferPageActions.resetDates, offerId: paginator?.currentOfferId })
  }, [dispatch, paginator])

  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false

  return <DropdownSheetFooter
    startSlot={bundleSelectedDays.available && <Group direction="vertical">
      <Caption variant="large">
        {pluralizeToString('night', bundleSelectedDays.duration)}{', 2 hotels '}
        {relevantPackages.length !== 1 && ' from'}
      </Caption>
      <div>
        <Heading as="span" variant="heading5">
          <FormatCurrency value={bundleSelectedDays.hotelTotal} />
        </Heading>{' '}
        <Caption as="span" variant="large">total</Caption>
      </div>

      {isBundleAndSaveV2Enabled() && <PriceRowTaxesAndFees />}
    </Group>}
    actionsSlot={<>
      {!!selectedDay && <TextButton kind="tertiary" size="large" onClick={onResetDates}>
        Reset dates
      </TextButton>}
      {isBundleAndSaveV2Enabled() && !!selectedDay && (!selectedPackageView || useDynamicCancellationPolicies) && <TextButton
        data-testid="offer-calendar-apply"
        kind="primary"
        size="large"
        onClick={onApply}
      >
        Continue
      </TextButton>}
      {!isBundleAndSaveV2Enabled() && !!selectedDay && (!selectedPackageView || useDynamicCancellationPolicies) && (
        <TextButton kind="primary" size="large" data-testid="offer-calendar-apply" onClick={onApply}>
          Continue
        </TextButton>
      )}
    </>}
  />
}

export default withOfferPageAndReduxState((state, appState) => ({
  bundleSelectedDays: getBundleSelectedDays(appState, state),
}))(OfferBundleAndSaveCalendarDatePickerDropdownFooter)
